import { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Rocket, KeyRound, Copy, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";

const PENDING_KEY = "pending_demo_upgrade";        // D2: Demo → Produktiv
const PENDING_CREDS_KEY = "pending_demo_creds";    // D1.2: Wizard-Demo

// D1.4 (Go-Live) – Legacy-D2-Upgrade-Banner standardmäßig stillgelegt. Nur bei
// REACT_APP_DEMO_UPGRADE_ENABLED="true" sichtbar. Das D1.2-Zugangsdaten-Modal
// unten ist davon NICHT betroffen und bleibt in jedem Fall aktiv.
const DEMO_UPGRADE_ENABLED =
  process.env.REACT_APP_DEMO_UPGRADE_ENABLED === "true";

const readJson = (key) => {
  try {
    return JSON.parse(localStorage.getItem(key) || "null");
  } catch {
    return null;
  }
};

/**
 * Phase D2 – Demo → eigener Betrieb.
 * - Banner-CTA „Eigenen Betrieb einrichten" (nur in einer Demo-Session).
 * - Nach Wizard-Abschluss im Produktivmandanten: Zugangsdaten-Modal mit aktiver
 *   Bestätigung, danach Entfernung der Ursprungs-Demo (bestehende factory_reset-Logik).
 */
export default function DemoUpgrade({ ownerSession, company, onDone }) {
  const [starting, setStarting] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [copied, setCopied] = useState(null);

  const isDemo = !!ownerSession?.tenant_id?.startsWith("demo-");
  const pending = readJson(PENDING_KEY);            // D2
  const pendingCreds = readJson(PENDING_CREDS_KEY); // D1.2 (Wizard-Demo)

  // D2-Zugangsdaten-Modal: Upgrade ausstehend UND Produktiv-Wizard abgeschlossen.
  const showUpgradeCreds =
    !!pending && !!company?.setup_completed && ownerSession?.tenant_id === pending.prod_tenant_id;
  // D1.2-Zugangsdaten-Modal: frische Wizard-Demo, Wizard abgeschlossen, gleicher Tenant.
  const showWizardCreds =
    !pending && !!pendingCreds && !!company?.setup_completed &&
    ownerSession?.tenant_id === pendingCreds.demo_tenant_id;
  const showCreds = showUpgradeCreds || showWizardCreds;
  // Quelle für die angezeigten Werte (beide Pfade tragen company_code + owner_pin).
  const creds = showUpgradeCreds ? pending : pendingCreds;

  const handleUpgrade = async () => {
    if (starting) return;
    setStarting(true);
    try {
      const res = await api.demoUpgrade();
      // AUTH-SICHERHEIT V2 · Phase 2: KEIN token mehr — das Session-Cookie
      // wurde vom Backend im Upgrade-Response gesetzt. Wir persistieren nur
      // die Anzeigedaten der neuen Produktiv-Owner-Session.
      localStorage.setItem(
        "owner_session",
        JSON.stringify({
          tenant_id: res.tenant_id,
          company_name: res.company_name,
          company_code: res.company_code,
          must_change_pin: !!res.must_change_pin,
        })
      );
      // … und Zugangsdaten + Herkunft bis zur Bestätigung nach Wizard sichern.
      localStorage.setItem(
        PENDING_KEY,
        JSON.stringify({
          demo_tenant_id: res.demo_tenant_id,
          prod_tenant_id: res.tenant_id,
          company_code: res.company_code,
          owner_pin: res.owner_pin,
        })
      );
      window.location.href = "/unternehmer";
    } catch (e) {
      setStarting(false);
      toast.error(
        e?.response?.data?.detail || "Der Betrieb konnte nicht erstellt werden."
      );
    }
  };

  const handleConfirmCreds = async () => {
    if (confirming) return;
    setConfirming(true);
    try {
      if (showUpgradeCreds) {
        // D2: Ursprungs-Demo nach Wizard-Abschluss entfernen (bestehende Logik).
        await api.discardDemoAfterUpgrade(pending.demo_tenant_id);
        localStorage.removeItem(PENDING_KEY);
        toast.success("Ihr Betrieb ist startklar. Viel Erfolg!");
      } else {
        // D1.2: nur die clientseitig gemerkten Zugangsdaten verwerfen (KEINE Löschung).
        localStorage.removeItem(PENDING_CREDS_KEY);
        toast.success("Zugangsdaten gespeichert.");
      }
      onDone?.();
      // Sauberer Neustart ohne Demo-Reste im Speicher.
      window.location.href = "/unternehmer";
    } catch (e) {
      setConfirming(false);
      toast.error(
        e?.response?.data?.detail ||
          "Die Demo konnte nicht entfernt werden. Bitte erneut bestätigen."
      );
    }
  };

  const copy = (value, key) => {
    try {
      navigator.clipboard.writeText(value);
      setCopied(key);
      setTimeout(() => setCopied(null), 1500);
    } catch { /* clipboard optional */ }
  };

  return (
    <>
      {/* D1.4: Legacy-D2-Banner per Feature-Flag stillgelegt (Default aus).
          D1.3: zusätzlich nie in einer Wizard-Demo (pendingCreds gesetzt). */}
      {DEMO_UPGRADE_ENABLED && isDemo && !pending && !pendingCreds && (
        <div
          className="mb-6 rounded-lg border border-slate-800 bg-slate-900 text-white px-4 py-4 sm:px-5 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4"
          data-testid="demo-upgrade-banner"
        >
          <div className="flex-1 min-w-0">
            <div className="font-heading font-semibold flex items-center gap-2">
              <Rocket className="w-4 h-4 text-amber-300" /> Das ist eine Demo zum Ausprobieren
            </div>
            <div className="text-slate-300 text-sm mt-0.5">
              Richten Sie in wenigen Minuten Ihren echten Betrieb ein – die Demo wird danach automatisch entfernt.
            </div>
          </div>
          <Button
            onClick={handleUpgrade}
            disabled={starting}
            data-testid="demo-upgrade-button"
            className="bg-amber-400 hover:bg-amber-300 text-slate-900 font-semibold shrink-0"
          >
            {starting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Rocket className="w-4 h-4" />}
            Eigenen Betrieb einrichten
          </Button>
        </div>
      )}

      <Dialog open={showCreds} onOpenChange={() => {}}>
        <DialogContent
          className="sm:max-w-md"
          data-testid="demo-creds-modal"
          onPointerDownOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
          hideClose
        >
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <KeyRound className="w-5 h-5 text-slate-700" /> Ihre Zugangsdaten
            </DialogTitle>
            <DialogDescription>
              Ihr Betrieb ist eingerichtet. Bitte notieren Sie diese Zugangsdaten –
              Sie brauchen sie beim nächsten Login.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-1">
            <div className="rounded-md border border-slate-200 bg-slate-50 p-3 flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-widest text-slate-400">Firmencode</div>
                <div className="font-mono text-lg font-semibold text-slate-900" data-testid="demo-creds-company-code">
                  {creds?.company_code}
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={() => copy(creds?.company_code, "code")} data-testid="demo-creds-copy-code">
                {copied === "code" ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
              </Button>
            </div>
            <div className="rounded-md border border-slate-200 bg-slate-50 p-3 flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-widest text-slate-400">Temporäre PIN</div>
                <div className="font-mono text-lg font-semibold text-slate-900" data-testid="demo-creds-pin">
                  {creds?.owner_pin}
                </div>
              </div>
              <Button variant="ghost" size="sm" onClick={() => copy(creds?.owner_pin, "pin")} data-testid="demo-creds-copy-pin">
                {copied === "pin" ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
              </Button>
            </div>
            <p className="text-xs text-slate-500">
              Beim nächsten Login werden Sie aufgefordert, eine eigene PIN zu vergeben.
              Den Firmencode finden Sie jederzeit unter „Einstellungen".
            </p>
          </div>

          <Button
            onClick={handleConfirmCreds}
            disabled={confirming}
            data-testid="demo-creds-confirm"
            className="w-full bg-slate-900 hover:bg-slate-800 text-white"
          >
            {confirming ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            Zugangsdaten gespeichert
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
}
